"use client";

import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
  useRef,
} from "react";

import { cohesion } from "@redventures/cohesion-utils-react";
import { callMonarchApi } from "@/lib/cohesion/monarchApi";

interface RuleState<T = unknown> {
  isLoading: boolean;
  value: T;
}

type State = Record<string, RuleState>;

interface RequestSuccessAction {
  type: "REQUEST_SUCCESS";
  key: string;
  value: unknown;
}

interface RequestFailureAction {
  type: "REQUEST_FAILURE";
  key: string;
}

type Action = RequestSuccessAction | RequestFailureAction;

// Define types for the context
interface MonarchRuleContextType {
  state: State;
  fetchRule: (
    ruleName: string,
    params: Record<string, unknown> | undefined,
  ) => void;
}

const MonarchRuleContext = createContext<MonarchRuleContextType | undefined>(
  undefined,
);

//https://github.com/RedVentures/cohesion-client-js/blob/3d1b5ee33e1c8275d2c58977206eda44759a0164/src/libs/monarch/utils.js#L69
export function updatePayloadWithPreviews(payload: Record<string, any>): void {
  const url = new URL(window.location.href);
  const monarchPreviewString = url.searchParams.get("monarchPreview");

  const isStrict = url.searchParams.get("monarchPreviewStrict") === "1";
  const previewTuples = monarchPreviewString
    ? monarchPreviewString.split(";").map((s) => s.split("."))
    : [];
  const previews: { [key: string]: string } = {};
  for (let i = 0; i < previewTuples.length; i++) {
    const previewTuple = previewTuples[i];
    if (i === 0 && previewTuple.length === 1) {
      // Allow the first tuple to implicitly be for the `trafficFlow`.
      previewTuple.unshift("trafficFlow");
    } else if (previewTuple.length !== 2) {
      // Otherwise, require all tuples to have exactly two parts.
      continue;
    }
    const [ruleId, returnValueId] = previewTuple;
    previews[ruleId] = returnValueId;
  }
  if (Object.keys(previews).length !== 0) {
    if (!payload.requestOptions) payload.requestOptions = {};
    if (!payload.requestOptions.forceReturns)
      payload.requestOptions.forceReturns = {};
    payload.requestOptions.forceReturns = {
      ...payload.requestOptions.forceReturns,
      ...previews,
    };
    if (!isStrict) {
      payload.requestOptions.allowInvalidForcedReturns = true;
    }
  }
  const monarchDraftPreviewString = url.searchParams.get("monarchDraft");
  const draftPreviews = monarchDraftPreviewString
    ? monarchDraftPreviewString.split(";")
    : [];
  if (draftPreviews.length !== 0) {
    if (!payload.requestOptions) payload.requestOptions = {};
    if (!payload.requestOptions.draftPreviewIds)
      payload.requestOptions.draftPreviewIds = [];
    payload.requestOptions.draftPreviewIds = [
      ...payload.requestOptions.draftPreviewIds,
      ...draftPreviews,
    ];
  }
}

// Reducer function
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "REQUEST_SUCCESS":
      return {
        ...state,
        [action.key]: { isLoading: false, value: action.value },
      };
    case "REQUEST_FAILURE":
      return {
        ...state,
        [action.key]: { isLoading: false, value: undefined },
      };
    default:
      return state;
  }
};

interface MonarchRuleProviderProps {
  children: ReactNode;
}

export const MonarchRuleProvider: React.FC<MonarchRuleProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, {});
  const initialized = useRef(false);
  const queue = useRef<
    Array<{ ruleName: string; params: Record<string, unknown> | undefined }>
  >([]);

  // Swtiched to use ref instead of state to track in-flight requests
  // React's state updates are asynchronous and batched, which can lead to race conditions
  // Multiple components mounting simultaneously need immediate access to the request status
  // The ref provides synchronous access to the current request status
  // This prevents duplicate API calls when many components request the same rule simultaneously
  const inFlightRequests = useRef<Set<string>>(new Set());

  const processQueue = (
    cb: (params: {
      ruleName: string;
      params: Record<string, unknown> | undefined;
    }) => void,
  ) => {
    const uniqueQueue = Array.from(
      new Set(
        queue.current.map(({ ruleName, params }) =>
          JSON.stringify({ ruleName, params }),
        ),
      ),
    ).map((item) => JSON.parse(item));

    uniqueQueue.forEach(cb);
  };

  useEffect(() => {
    if (initialized.current) return;

    setTimeout(() => {
      if (!initialized.current) {
        processQueue(({ ruleName, params }) => {
          dispatch({
            type: "REQUEST_FAILURE",
            key: JSON.stringify({ ruleName, params }),
          });
        });
      }
    }, 2000);

    cohesion("ready", function () {
      initialized.current = true;
      processQueue(({ ruleName, params }) => {
        fetchRule(ruleName, params);
      });

      queue.current = [];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRule = async (
    ruleName: string,
    params?: Record<string, unknown> | undefined,
  ) => {
    const key = JSON.stringify({ ruleName, params });

    // Synchronously check if this request is already in flight
    if (inFlightRequests.current.has(key)) {
      return;
    }

    if (!initialized.current) {
      queue.current.push({ ruleName, params });
      return;
    }

    // Synchronously mark this request as in flight
    inFlightRequests.current.add(key);

    const payload = (params ?? {}) as Record<string, any>;

    try {
      updatePayloadWithPreviews(payload);

      const result = await callMonarchApi(ruleName, payload);

      if (!result) {
        throw new Error("Network response was not ok");
      }

      dispatch({ type: "REQUEST_SUCCESS", key, value: result[ruleName] });
    } catch (error) {
      window.newrelic?.noticeError(error as Error, {
        monarchRuleName: ruleName,
      });
      console.error("Failed to fetch Monarch rule:", error);
      dispatch({ type: "REQUEST_FAILURE", key });
    } finally {
      // Always clean up the in-flight request tracking
      inFlightRequests.current.delete(key);
    }
  };

  return (
    <MonarchRuleContext.Provider value={{ state, fetchRule }}>
      {children}
    </MonarchRuleContext.Provider>
  );
};

export type UseMonarchRuleOptions = {
  /**
   * When false, the rule is not fetched and the hook returns an idle result.
   * Use to gate Cohesion rules behind feature flags or other conditions.
   */
  enabled?: boolean;
};

export function useMonarchRule<T>(
  ruleName: string,
  params?: Record<string, unknown>,
  options?: UseMonarchRuleOptions,
): RuleState<T> {
  const context = useContext(MonarchRuleContext);
  if (!context) {
    throw new Error("useMonarchRule must be used within a MonarchRuleProvider");
  }

  const { state, fetchRule } = context;
  const key = JSON.stringify({ ruleName, params });
  const enabled = options?.enabled !== false;

  // Store the monarch enabled state
  const isMonarchDisabled = () => {
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      return url.searchParams.get("monarchEnabled") === "false";
    }
    return false;
  };

  useEffect(() => {
    if (!enabled) {
      return;
    }
    // Skip fetching if monarch is disabled via query param
    if (isMonarchDisabled()) {
      return;
    }
    if (!state[key]) {
      fetchRule(ruleName, params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ruleName, params, state, enabled, isMonarchDisabled]);

  if (!enabled || isMonarchDisabled()) {
    return {
      isLoading: false,
      value: undefined as unknown as T,
    };
  }

  return (
    (state[key] as unknown as RuleState<T>) ?? {
      isLoading: true,
      value: undefined,
    }
  );
}
